import React, { useState, useContext, useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { AuthContext } from 'Auth/AuthContext';

import Modal from 'components/Modal/Modal';

import BillingPolicyDraft from 'Customers/BillingCapacity/BillingPolicyDraft';

import { capacityChanged } from 'Customers/BillingCapacity/BillingCapacityMove';

import { moveLabels } from 'Customers/BillingCapacity/moveTranslations';

import { useQueryGetUsers } from 'Users/hooks/useUsers';

import EditSubscriptionForm from './EditSubscriptionForm';

import { useMutationUpdateSubscription } from '../../hooks/useSubscriptions';

const EditSubscriptionModal = ({ isOpen, onClose, selected, billingPreview, onPurchaseComplete }) => {
  const { authState } = useContext(AuthContext) || {};
  const queryClient = useQueryClient();
  const { data: users } = useQueryGetUsers({ currentPage: 1, perPage: 9999 });
  const [reviewing, setReviewing] = useState(false);
  const [sent, setSent] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const { mutateAsync: UpdateMutate } = useMutationUpdateSubscription();

  useEffect(() => {
    if (isOpen) setReviewing(false);
  }, [isOpen, billingPreview?.previewId, selected.id]);

  const initialValues = {
    price: selected?.price,
    numberOfUnits: selected?.numberOfUnits,
    implementationUser: selected?.implementationUser || '',
  };

  const handleSubmit = async (data) => {
    setIsSending(true);
    try {
      await UpdateMutate({ id: selected.id, data });
      setSent(true);
      await queryClient.invalidateQueries({ queryKey: ['TenantSubscriptions'] }, { exact: true });
      if (data.quantityOnly) {
        capacityChanged();
        if (billingPreview.setupRecovery)
          onPurchaseComplete?.({ before: billingPreview.purchased.active, after: data.numberOfUnits });
        onClose();
      }
    } finally {
      setIsSending(false);
    }
  };
  return (
    <Modal
      isOpen={isOpen}
      direction={authState?.user?.language === 'ar' ? 'rtl' : 'ltr'}
      shouldCloseOnOverlayClick={!billingPreview && !isSending}
      showHeader={!reviewing}
      title={billingPreview ? moveLabels[authState?.user?.language === 'ar' ? 'ar' : 'en'].edit : 'Edit Subscription'}
      styles={{
        width: reviewing ? '720px' : '560px',
        maxWidth: 'calc(100vw - 24px)',
        padding: 0,
      }}
      onRequestClose={() => {
        if (!isSending) onClose();
      }}
    >
      {isOpen && (
        <EditSubscriptionForm
          key={billingPreview?.previewId || selected.id}
          billingPreview={billingPreview}
          onReviewChange={setReviewing}
          onClose={onClose}
          onSubmit={handleSubmit}
          isSending={isSending}
          initialValues={initialValues}
          selected={selected}
          sent={sent}
          users={users?.data?.items || []}
        />
      )}
      {isOpen && !billingPreview && selected?.isPrimary && (
        <BillingPolicyDraft subscriptionId={selected.id} plan={selected.plan} />
      )}
    </Modal>
  );
};

export default EditSubscriptionModal;
